import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectSelectedEntity, selectEditorOperationLoading, cancelInteraction } from '@/store/slices/editorSlice';
import { updateCustomMapDetails, deleteCustomMap } from '@/store/slices/customMapsSlice';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CustomMapEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CustomMapEditDialog: React.FC<CustomMapEditDialogProps> = ({
  open,
  onOpenChange
}) => {
  const dispatch = useAppDispatch();
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const isSaveLoading = useAppSelector(selectEditorOperationLoading) === 'pending';

  const [editingMap, setEditingMap] = useState<{
    id: string;
    name: string;
    description?: string;
    isPublic: boolean;
  } | null>(null);
  
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchMapDetails = async () => {
      if (open && selectedEntity?.type === 'customMap' && selectedEntity.id) {
        setIsFetchingDetails(true);
        setFetchError(null);
        setEditingMap(null);
        try {
          const mapDocRef = doc(db, 'customMaps', selectedEntity.id);
          const docSnap = await getDoc(mapDocRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setEditingMap({
              id: docSnap.id,
              name: data.name || '',
              description: data.description || '',
              isPublic: data.isPublic || false
            });
          } else {
            setFetchError("Custom map not found.");
            console.error("Error: Custom map document not found for ID:", selectedEntity.id);
          }
        } catch (error) {
          console.error("Error fetching custom map details:", error);
          setFetchError("Failed to load custom map details.");
        } finally {
          setIsFetchingDetails(false);
        }
      } else if (!open) {
        setEditingMap(null);
        setFetchError(null);
      }
    };

    fetchMapDetails();
  }, [open, selectedEntity]);

  // Handle keyboard escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    if (open) {
      window.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, onOpenChange]);

  // Handle field changes
  const handleNameChange = (value: string) => {
    setEditingMap((prev) => prev ? { ...prev, name: value } : null);
  };

  const handleDescriptionChange = (value: string) => {
    setEditingMap((prev) => prev ? { ...prev, description: value } : null);
  };

  const handlePublicChange = (checked: boolean) => {
    setEditingMap((prev) => prev ? { ...prev, isPublic: checked } : null);
  };

  // Handle save
  const handleSave = async () => {
    if (!editingMap || isSaveLoading) return;
    
    try {
      // Validate map name
      if (!editingMap.name.trim()) {
        toast.error("Map name cannot be empty");
        return;
      }
      
      await dispatch(updateCustomMapDetails({
        mapId: editingMap.id,
        name: editingMap.name,
        description: editingMap.description,
        isPublic: editingMap.isPublic
      })).unwrap();
      
      toast.success("Custom map updated successfully");
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving custom map:', error);
      toast.error(typeof error === 'string' ? error : "Failed to update custom map");
    }
  };

  // Add the delete handler
  const handleDeleteMap = async () => {
    if (!editingMap || deleting) return;
    
    setDeleting(true);
    try {
      await dispatch(deleteCustomMap(editingMap.id)).unwrap();
      toast.success("Custom map deleted successfully");
      // Close both dialogs
      setDeleteDialogOpen(false);
      onOpenChange(false);
      // Ensure the editor interaction state is cleared
      dispatch(cancelInteraction());
    } catch (error: any) {
      console.error('Error deleting custom map:', error);
      toast.error(typeof error === 'string' ? error : "Failed to delete custom map");
    } finally {
      setDeleting(false);
    }
  };

  // Enhanced dialog cleanup on close
  const handleOpenChangeInternal = (newOpenState: boolean) => {
    onOpenChange(newOpenState);
  };

  const handleCancel = () => {
    handleOpenChangeInternal(false);
  };

  if (!open) return null;

  if (isFetchingDetails) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px] flex items-center justify-center">
          <DialogHeader>
            <DialogTitle>Loading Data</DialogTitle>
            <DialogDescription>
              Please wait while the custom map details are being loaded.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="mr-2 h-8 w-8 animate-spin" />
            <span>Loading custom map details...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (fetchError) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>{fetchError}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (!editingMap && selectedEntity?.type === 'customMap') {
     return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>Could not load custom map data to edit.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!editingMap) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Custom Map</DialogTitle>
            <DialogDescription>
              Make changes to your custom map.
            </DialogDescription>
          </DialogHeader>
          
          <div className={`grid gap-4 py-4 ${isSaveLoading || deleting ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">Name</Label>
              <Input 
                id="name" 
                value={editingMap.name} 
                onChange={(e) => handleNameChange(e.target.value)}
                className="col-span-3"
                disabled={isSaveLoading || deleting}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">Description</Label>
              <Textarea 
                id="description" 
                value={editingMap.description || ''} 
                onChange={(e) => handleDescriptionChange(e.target.value)}
                placeholder="Optional description for your map"
                className="col-span-3"
                disabled={isSaveLoading || deleting}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="public" className="text-right">Public</Label>
              <div className="flex items-center space-x-2 col-span-3">
                <Switch 
                  id="public" 
                  checked={editingMap.isPublic} 
                  onCheckedChange={handlePublicChange}
                  disabled={isSaveLoading || deleting}
                />
                <Label htmlFor="public" className="cursor-pointer">
                  {editingMap.isPublic ? 'This map is public and visible to everyone' : 'This map is private and only visible to you'}
                </Label>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isSaveLoading || deleting}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Map
            </Button>
            <div className="flex gap-2">
              <DialogClose asChild>
                <Button variant="outline" onClick={handleCancel} disabled={isSaveLoading || deleting}>Cancel</Button>
              </DialogClose>
              <Button onClick={handleSave} disabled={isSaveLoading || deleting || isFetchingDetails}>
                {isSaveLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : "Save Changes"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
              Delete Custom Map
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this map? This action cannot be undone
              and all locations saved to this map will be removed from it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteMap}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {deleting ? 'Deleting...' : 'Yes, Delete Map'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}; 